import { IChartBackendService, ISorter, RawChartDataLike, ChartConfigLike } from "./types";

export class StackedAreaChartBackendServiceTS {
  private readonly chartBackendService: IChartBackendService;
  private readonly sorter: ISorter;

  constructor(chartBackendService: IChartBackendService, sorter: ISorter) {
    this.chartBackendService = chartBackendService;
    this.sorter = sorter;
  }

  async fetchJson(tableId: string | number, filter: any, config: any, isBiView?: boolean): Promise<any> {
    return this.chartBackendService.fetchJson(tableId, filter, config, isBiView);
  }

  getRepresentation(resultData: RawChartDataLike, config: ChartConfigLike) {
    let data: any[] = [];

    const multipleSeries = (config as any).chartSeries && (config as any).chartSeries.fieldName;

    const seriesType = multipleSeries ? resultData.config.series.data_type : resultData.config.key.data_type;
    const xAxisType = resultData.config.key.data_type;
    const valueType = resultData.config.values.data_type;

    const seriesParser = this.chartBackendService.getParserForType(seriesType);
    const xAxisParser = this.chartBackendService.getParserForType(xAxisType);
    const valueParser = this.chartBackendService.getParserForType(valueType);

    let uniqueXValues: any[] = [];
    data = this.chartBackendService
      .nest()
      .key(d => (multipleSeries ? seriesParser(d.series) : ""))
      .rollup((d: any[]) => {
        for (let i = 0, l = d.length; i < l; i++) {
          const parsedSubCat = xAxisParser(d[i].key);
          if (uniqueXValues.indexOf(parsedSubCat) === -1) {
            uniqueXValues.push(parsedSubCat);
          }
          d[i] = { x: parsedSubCat, y: valueParser(d[i].values[0]) };
        }
        return this.sorter.sort(d, {
          dataType: xAxisType,
          valueParser: (item: any) => item.x,
        });
      })
      .excludeNullKeys(true)
      .entries(resultData.values);

    uniqueXValues = this.sorter.sort(uniqueXValues, { dataType: xAxisType });
    const xToScalar = xAxisType === "character" ? (x: any) => uniqueXValues.indexOf(x) : (x: any) => x;

    const insertInterpolation = (x: any, dataArr: any[], index: number) => {
      if (dataArr.length === 0) {
        dataArr.push({ x: x, y: null, interpolated: true });
        return;
      } else if (dataArr.length === 1 || index === 0 || index === dataArr.length) {
        if (index === 0) {
          dataArr.splice(0, 0, { x: x, y: dataArr[0].y, interpolated: true });
        } else {
          dataArr.push({ x: x, y: dataArr[index - 1].y, interpolated: true });
        }
      } else {
        const y =
          dataArr[index - 1].y + //previous y
          (dataArr[index].y - dataArr[index - 1].y) * //total change in y
            ((xToScalar(x) - xToScalar(dataArr[index - 1].x)) /
              (xToScalar(dataArr[index].x) - xToScalar(dataArr[index - 1].x))); //percent change of x
        dataArr.splice(index, 0, { x: x, y: y, interpolated: true });
      }
    };

    for (let x = 0; x < uniqueXValues.length; x++) {
      for (let cat = 0; cat < data.length; cat++) {
        let v;
        for (v = 0; v < data[cat].values.length; v++) {
          if (data[cat].values[v].x === uniqueXValues[x]) {
            break;
          }
          if (xToScalar(data[cat].values[v].x) > xToScalar(uniqueXValues[x])) {
            insertInterpolation(uniqueXValues[x], data[cat].values, v);
            break;
          }
        }
        if (v === data[cat].values.length) {
          insertInterpolation(uniqueXValues[x], data[cat].values, v);
        }
      }
    }

    // Sort the series
    data = this.sorter.sort(data, {
      dataType: seriesType,
      valueParser: (item: any) => item.key,
    });

    return data;
  }
}

export function createAngularCompatibleStackedAreaService(
  chartBackendService: IChartBackendService,
  sorter: ISorter
) {
  const service = new StackedAreaChartBackendServiceTS(chartBackendService, sorter);
  return {
    fetchJson: service.fetchJson.bind(service),
    getRepresentation: service.getRepresentation.bind(service),
  };
}

